import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { HandbookBlock } from './model';

/**
 * Ein Bildschirmfoto aus ``public/handbuch/``.
 *
 * **Eigene Komponente und nicht in ``Block`` eingebettet.** Eine innerhalb
 * von ``Block`` deklarierte Komponente bekaeme bei jedem Neuzeichnen eine
 * neue Identitaet; React haengte sie dann aus und wieder ein, und der
 * Fehlerzustand unten setzte sich jedes Mal zurueck -- die Aufnahme wuerde
 * endlos neu angefordert.
 *
 * Faellt das Bild aus, bleibt die Bildunterschrift stehen. Ein Handbuch mit
 * einem kaputten Bildsymbol sieht nach einem Fehler aus; eines mit einer
 * Beschreibung und dem Hinweis, dass die Aufnahme fehlt, ist lesbar.
 */
function Screenshot({ file, caption }: { file: string; caption: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <figure className="handbook__shot handbook__shot--missing">
        <p className="field__hint">{t('handbook.shotMissing')}</p>
        <figcaption>{caption}</figcaption>
      </figure>
    );
  }

  return (
    <figure className="handbook__shot">
      <img
        src={`/handbuch/${file}.png`}
        alt={caption}
        loading="lazy"
        onError={() => {
          setFailed(true);
        }}
      />
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

/**
 * Die Darstellung der Bausteine.
 *
 * Kein eigenes Aussehen: Alles hier benutzt die Bausteine, die auch die
 * uebrige Oberflaeche benutzt -- ``notice``, ``table``, ``definition-list``.
 * Ein Handbuch, das anders aussieht als die Anwendung, die es beschreibt,
 * ist eine zweite Oberflaeche, die man mitpflegen muss.
 */
export function Block({ block }: { block: HandbookBlock }) {
  switch (block.kind) {
    case 'text':
      return <p className="handbook__text">{block.text}</p>;

    case 'steps':
      return (
        <ol className="handbook__steps">
          {block.steps.map((step, index) => (
            <li key={index}>
              <span className="handbook__step-text">{step.text}</span>
              {step.detail ? <span className="handbook__step-detail">{step.detail}</span> : null}
            </li>
          ))}
        </ol>
      );

    case 'note':
      return (
        <div className={`notice notice--${block.tone}`}>
          <p className="notice__title">{block.title}</p>
          <p>{block.text}</p>
        </div>
      );

    case 'shot':
      return <Screenshot file={block.file} caption={block.caption} />;

    case 'table':
      return (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                {block.head.map((cell) => (
                  <th key={cell} scope="col">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, index) => (
                <tr key={index}>
                  {row.map((cell, cellIndex) =>
                    // Die erste Spalte ist der Zeilenkopf. Ohne diese
                    // Auszeichnung liest ein vorlesendes Programm in Zeile
                    // vierzig nur noch Werte ohne die Angabe, wozu sie
                    // gehoeren.
                    cellIndex === 0 ? (
                      <th key={cellIndex} scope="row" className="handbook__row-head">
                        {cell}
                      </th>
                    ) : (
                      <td key={cellIndex}>{cell}</td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'terms':
      return (
        <dl className="definition-list handbook__terms">
          {block.items.map((item) => (
            <div key={item.term}>
              <dt>{item.term}</dt>
              <dd>{item.text}</dd>
            </div>
          ))}
        </dl>
      );

    case 'route':
      return (
        <p className="handbook__route">
          {/* Der Sprung in die beschriebene Maske. `to` ist eine Zeichenkette
              aus den Kapiteldaten und laesst sich vom Router nicht statisch
              pruefen -- deshalb der Abgleich in tests/Handbook.test.tsx, der
              jede Adresse gegen den Routenbaum haelt. */}
          <Link to={block.to} className="handbook__route-link">
            {block.label}
            <span aria-hidden="true"> →</span>
          </Link>
          {block.hint ? <span className="field__hint">{block.hint}</span> : null}
        </p>
      );

    case 'example':
      return (
        <div className="handbook__example">
          <p className="handbook__example-title">{block.title}</p>
          <p>{block.text}</p>
        </div>
      );
  }
}
